import { Reservation } from '@backend/domain'
import { ReservationRepository } from '@backend/repository'
import { BookingStatus } from '@core/google-reservations/types/booking-server'
import { AnyObject, ReservationState, StageId } from '@core/types'
import { Processor, WorkerHost } from '@nestjs/bullmq'
import { Logger } from '@nestjs/common'
import { differenceInSeconds } from 'date-fns'
import { JWT } from 'google-auth-library'
import { TracedProcessor } from '../../../../../common'
import { GOOGLE, Stage } from '../../../../../config'
import { GOOGLE_RESERVATIONS_QUEUES, GoogleReatimeUpdateJob } from '../../../../../core'
import { ReservationUpdates } from '../../../types'
import { getServiceId } from '../utils'

const SANDBOX_ENDPOINT = 'https://partnerdev-mapsbooking.googleapis.com'
const PROD_ENDPOINT = 'https://mapsbooking.googleapis.com'
const SCOPES = ['https://www.googleapis.com/auth/mapsbooking']

@Processor(GOOGLE_RESERVATIONS_QUEUES.SEND_REALTIME_UPDATE)
export class RealtimeUpdateProcessor extends WorkerHost {
	private readonly logger = new Logger(RealtimeUpdateProcessor.name)

	private readonly requestClient: JWT

	constructor(private readonly _reservationRepository: ReservationRepository) {
		super()

		this.requestClient = new JWT({
			email: GOOGLE.ACTION_CENTER.SERVICE_ACCOUNT.CLIENT_EMAIL,
			key: GOOGLE.ACTION_CENTER.SERVICE_ACCOUNT.PRIVATE_KEY,
			scopes: SCOPES,
		})
	}

	@TracedProcessor()
	async process(job: GoogleReatimeUpdateJob): Promise<void> {
		const { reservationId, updates } = job.data

		await this.sendBookingNotification(reservationId, updates)
	}

	private async sendBookingNotification(reservationId: string, updates: ReservationUpdates): Promise<void> {
		const reservation = await this._reservationRepository.findOne({ where: { id: reservationId } })

		if (!reservation) {
			this.logger.error({ message: 'Reservation not found', reservationId })
			return
		}

		const { mask, body } = this.prepareRequestParams(reservation, updates)

		try {
			await this.postUpdate(mask, body)
		} catch (error) {
			this.logger.error({ message: 'Failed to post realtime update to Google', error: error.message })
			throw error
		}

		this.logger.log({ message: 'Realtime update for reservation sent to google', reservationId, mask, body })
	}

	private prepareRequestParams(
		reservation: Reservation,
		updates: ReservationUpdates,
	): { mask: string; body: AnyObject } {
		const merchantId = reservation.tenantId!

		const body: AnyObject = {
			merchantId,
			serviceId: getServiceId(merchantId),
			name: `partners/${GOOGLE.ACTION_CENTER.PARTNER_ID}/bookings/${reservation.id}`,
		}

		const mask: string[] = []

		const statusUpdate = updates.some((update) => update?.field === 'state')
		const startTimeUpdate = updates.some((update) => update?.field === 'startDate')
		const durationUpdate = updates.some((update) => update?.field === 'startDate' || update?.field === 'endDate')
		const partySizeUpdate = updates.some((update) => update?.field === 'noOfPersons')

		if (statusUpdate) {
			if (reservation.state === ReservationState.rejected) {
				mask.push('status')
				body.status = BookingStatus.CANCELED
			} else if (reservation.state === ReservationState.approved) {
				mask.push('status')
				body.status = BookingStatus.CONFIRMED
			}
		}

		if (startTimeUpdate) {
			mask.push('startTime')
			body.startTime = reservation.startDate.toISOString()
		}

		if (durationUpdate) {
			const duration = differenceInSeconds(reservation.endDate, reservation.startDate)
			mask.push('duration')
			body.duration = `${duration}s`
		}

		if (partySizeUpdate) {
			mask.push('partySize')
			body.partySize = reservation.noOfPersons
		}

		return { mask: mask.join(','), body }
	}

	private async postUpdate(fieldMask: string, body: AnyObject): Promise<void> {
		const url = `${Stage === StageId.PRODUCTION ? PROD_ENDPOINT : SANDBOX_ENDPOINT}/v1alpha/notification/${body.name}?updateMask=${fieldMask}`
		await this.requestClient.request({ url, data: body, method: 'PATCH' })
	}
}
